/* eslint-disable @typescript-eslint/naming-convention -- icon registry keys match SVG asset names and IconSize values */
import { JSX } from 'solid-js';
import { IconSize } from '../Icon/icon-size';
import svgArchiveRaw from './svg-archive.svg?raw';
import svgAssistantRaw from './svg-assistant.svg?raw';
import svgBcfAddRaw from './svg-bcf-add.svg?raw';
import svgCantRaw from './svg-cant.svg?raw';
import svgClipBoxRaw from './svg-clip-box.svg?raw';
import svgClipPlaneRaw from './svg-clip-plane.svg?raw';
import svgComputeVolumeRaw from './svg-compute-volume.svg?raw';
import svgCompactorRaw from './svg-compactor.svg?raw';
import svgCubeOutlineRaw from './svg-cube-outline.svg?raw';
import svgCutFillRaw from './svg-cut-fill.svg?raw';
import svgDesignPackageRaw from './svg-design-package.svg?raw';
import svgGaussianSplatsRaw from './svg-gaussian-splats.svg?raw';
import svgGroundworksQualityRaw from './svg-groundworks-quality.svg?raw';
import svgImageSceneOutlineRaw from './svg-image-scene-outline.svg?raw';
import svgInsertPhotoRaw from './svg-insert-photo.svg?raw';
import svgLineworkRaw from './svg-linework.svg?raw';
import svgMachineRaw from './svg-machine.svg?raw';
import svgMachinesRaw from './svg-machines.svg?raw';
import svgMapOutlineRaw from './svg-map-outline.svg?raw';
import svgPhoto360Raw from './svg-photo-360.svg?raw';
import svgPointCloudRaw from './svg-point-cloud.svg?raw';
import svgSlopeRaw from './svg-slope.svg?raw';
import svgSurfaceRaw from './svg-surface.svg?raw';
import svgTerrainRaw from './svg-terrain.svg?raw';
import svgVerticalRaw from './svg-vertical.svg?raw';
import svgVerticalClipPlaneRaw from './svg-vertical-clip-plane.svg?raw';
import svgXyRaw from './svg-xy.svg?raw';
import svgZRaw from './svg-z.svg?raw';

export type TempSvgIcon =
  | 'svg-cant'
  | 'svg-slope'
  | 'svg-compute-volume'
  | 'svg-cut-fill'
  | 'svg-xy'
  | 'svg-z'
  | 'svg-vertical'
  | 'svg-archive'
  | 'svg-assistant'
  | 'svg-bcf-add'
  | 'svg-cube-outline'
  | 'svg-map-outline'
  | 'svg-image-scene-outline'
  | 'svg-machines'
  | 'svg-machine'
  | 'svg-compactor'
  | 'svg-surface'
  | 'svg-photo-360'
  | 'svg-point-cloud'
  | 'svg-terrain'
  | 'svg-insert-photo'
  | 'svg-clip-plane'
  | 'svg-vertical-clip-plane'
  | 'svg-clip-box'
  | 'svg-linework'
  | 'svg-design-package'
  | 'svg-gaussian-splats'
  | 'svg-groundworks-quality';

interface SvgIconDef {
  readonly viewBox: string;
  readonly fill: string;
  readonly inner: string;
}

const parseSvg = (raw: string): SvgIconDef => {
  const viewBox = /viewBox\s*=\s*"([^"]+)"/.exec(raw)?.[1] ?? '0 0 24 24';
  const fill = /<svg[^>]*\sfill\s*=\s*"([^"]+)"/.exec(raw)?.[1] ?? 'none';
  const inner = /<svg[^>]*>([\s\S]*?)<\/svg>/.exec(raw)?.[1].trim() ?? '';
  return { viewBox, fill, inner };
};

const SVG_ICONS: Record<TempSvgIcon, SvgIconDef> = {
  'svg-archive': parseSvg(svgArchiveRaw),
  'svg-assistant': parseSvg(svgAssistantRaw),
  'svg-bcf-add': parseSvg(svgBcfAddRaw),
  'svg-cant': parseSvg(svgCantRaw),
  'svg-clip-box': parseSvg(svgClipBoxRaw),
  'svg-clip-plane': parseSvg(svgClipPlaneRaw),
  'svg-compute-volume': parseSvg(svgComputeVolumeRaw),
  'svg-compactor': parseSvg(svgCompactorRaw),
  'svg-cube-outline': parseSvg(svgCubeOutlineRaw),
  'svg-cut-fill': parseSvg(svgCutFillRaw),
  'svg-design-package': parseSvg(svgDesignPackageRaw),
  'svg-gaussian-splats': parseSvg(svgGaussianSplatsRaw),
  'svg-groundworks-quality': parseSvg(svgGroundworksQualityRaw),
  'svg-image-scene-outline': parseSvg(svgImageSceneOutlineRaw),
  'svg-insert-photo': parseSvg(svgInsertPhotoRaw),
  'svg-linework': parseSvg(svgLineworkRaw),
  'svg-machines': parseSvg(svgMachinesRaw),
  'svg-machine': parseSvg(svgMachineRaw),
  'svg-map-outline': parseSvg(svgMapOutlineRaw),
  'svg-photo-360': parseSvg(svgPhoto360Raw),
  'svg-point-cloud': parseSvg(svgPointCloudRaw),
  'svg-slope': parseSvg(svgSlopeRaw),
  'svg-surface': parseSvg(svgSurfaceRaw),
  'svg-terrain': parseSvg(svgTerrainRaw),
  'svg-vertical': parseSvg(svgVerticalRaw),
  'svg-vertical-clip-plane': parseSvg(svgVerticalClipPlaneRaw),
  'svg-xy': parseSvg(svgXyRaw),
  'svg-z': parseSvg(svgZRaw),
};

const ICON_SIZE_PX: Record<IconSize, number> = {
  'icon-small': 16,
  'icon-medium': 24,
  'icon-large': 32,
  'icon-xlarge': 48,
};

export const getSvgIcon = (
  iconName: TempSvgIcon,
  iconSize: IconSize
): JSX.Element => {
  const size = ICON_SIZE_PX[iconSize];
  const def = SVG_ICONS[iconName];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={def.viewBox}
      fill={def.fill}
      // eslint-disable-next-line solid/no-innerhtml -- def.inner is sourced from static .svg files imported via Vite `?raw` at build time; not user-controlled.
      innerHTML={def.inner}
    />
  );
};
